"use client";

import { useMemo } from "react";
import { type Columns, zipColumns } from "@/components/tools/example-columns";
import { StagedReflectionForm } from "@/components/tools/research-ops/staged-reflection-form";
import { useToolUi } from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const DRISCOLL_REFLECTIVE_MODEL_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  topicLabel: "What are you reflecting on?",
  topicPlaceholder:
    "e.g. Explaining discharge instructions to a patient's daughter",
  headingsLabel: "Give each stage its own heading",
  stagesHeading: "The three questions",
  stageFieldLabel: "{n}. {stage} — {question}",
  triggersHint: "Trigger questions: {questions}",
  wordCount: "one: word|other: words",
  fileName: "driscoll-reflection",
  document: {
    heading: "Your reflection",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: stage still to write|other: stages still to write",
    complete: "All three stages are written.",
  },
  defaultTitle: "Reflective Account",
  titleWithTopic: "Reflection: {topic}",
  subtitle: "Structured with Driscoll's reflective model (Driscoll, 2007)",
  voiceNote:
    "Write in the first person — “I did”, “I realized” — because the subject is your own practice. Keep “What?” brief, give most of your words to “So what?”, and make “Now what?” specific enough that someone could check you did it. In health care, change or omit anything that could identify a patient, a colleague, or a workplace.",
  stageHeading: "{n}. {stage}",
  stagePrompt: "{question} {guide}",
  stagePromptNamed: "{stage} — {question} {guide}",
  triggersLead: "Trigger questions:",
  stageNames: {
    what: "What?",
    sowhat: "So what?",
    nowwhat: "Now what?",
  },
  stageQuestions: {
    what: "What is the situation, and what happened?",
    sowhat: "What does it mean, and what explains it?",
    nowwhat: "What will you do as a result?",
  },
  stageGuides: {
    what: "Describe the event and your part in it: what you were trying to do, what you and others did, and what the outcome was. Say how you felt at the time. Keep to the facts.",
    sowhat:
      "Analyze it. Work out why it happened, what effect your actions had, and what you understand now that you did not before. Bring in theory, research, or professional standards — and cite them.",
    nowwhat:
      "Decide what to do differently. Name the actions, the wider factors that could help or block them, and how you will know whether they worked.",
  },
  stageTriggers: {
    what: [
      "What was my role, and what was I trying to achieve?",
      "What did I and the other people involved actually do?",
      "What were the consequences, and how did I feel?",
      "What went well, and what did not?",
    ],
    sowhat: [
      "Why did I act and feel the way I did?",
      "What effect did my action, or my inaction, have?",
      "What do guidelines, research, or theory say about this?",
      "What do I understand now, and what could I have done differently?",
    ],
    nowwhat: [
      "What do I need to do to improve things?",
      "What wider issues — colleagues, resources, policy — affect that?",
      "What might happen if I make this change?",
      "How will I know whether it has worked?",
    ],
  },
  referenceHeading: "References",
  referenceNote:
    "These are the APA 7 forms. Driscoll's model was first published in a 1994 journal article and developed in later editions of the book; cite the source you actually read, and check its year and publisher against your copy. Use the style your course requires.",
  references: {
    before: ["Borton, T. (1970). ", "Driscoll, J. (2007). "],
    title: [
      "Reach, touch, and teach: Student concerns and process education",
      "Practising clinical supervision: A reflective approach for healthcare professionals",
    ],
    after: [". McGraw-Hill.", " (2nd ed.). Elsevier."],
  } as Columns<"before" | "title" | "after">,
  example: {
    topic: "Explaining discharge instructions to a patient's daughter",
    what: "On my second day on the medical ward, my mentor asked me to explain a patient's discharge medication list to his daughter, who was collecting him. I read the list aloud in the order printed on it. She nodded, but when she repeated it back she had swapped the morning and evening tablets. My mentor corrected it before they left, and I felt embarrassed that she had needed to step in.",
    sowhat:
      "I think it went wrong because I treated the explanation as reading out a document rather than checking understanding. I used the drug names as printed and did not ask what the daughter already knew. Guidance on communicating with patients and carers recommends the teach-back method, where the listener explains the plan in their own words, and I did elicit that — but only at the end, too late to change how I had explained it. What I understand now is that nodding is not evidence of understanding, and that the order of information should follow what the person has to do, not the order on the chart.",
    nowwhat:
      "For my next three discharge conversations I will group the medicines by time of day, use plain names alongside the printed ones, and ask the carer to explain the first medicine back to me before I move on. I will ask my mentor to observe one of them and give feedback. The ward's pharmacist may have a written schedule template, so I will ask whether I can use it, which should make the plan easier to follow at home.",
  },
} as const;

const ORDER = ["what", "sowhat", "nowwhat"] as const;

type Strings = typeof DRISCOLL_REFLECTIVE_MODEL_GENERATOR_STRINGS;

/**
 * The strings as the staged-reflection builder wants them. `references` is stored as
 * columns (see `example-columns.ts`) and zipped back into rows here, English filling any
 * cell a translation left short.
 */
export function driscollLabels(strings: Strings) {
  return {
    ...strings,
    references: zipColumns(
      DRISCOLL_REFLECTIVE_MODEL_GENERATOR_STRINGS.references,
      strings.references
    ),
  };
}

export default function DriscollReflectiveModelGeneratorWidget() {
  const strings = useToolUi(DRISCOLL_REFLECTIVE_MODEL_GENERATOR_STRINGS);
  const labels = useMemo(() => driscollLabels(strings), [strings]);
  return (
    <StagedReflectionForm
      example={strings.example}
      form={strings}
      idPrefix="drm"
      labels={labels}
      order={ORDER}
    />
  );
}
